/** @jsxImportSource @emotion/react */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BaseButton from "components/common/Button/Base";
import { ExtendedOption } from "components/common/Select/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import BusinessAutocomplete from "../BusinessAutocomplete";
import { MatchCandidateBusiness } from "../SelectBusiness";
import {
  PaymentNotice,
  PaymentNoticeMatchResult,
} from "models/paymentNotice.model";
import { deletePaymentNotice, matchPaymentNotice } from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import { convertCurrency, convertDate, convertRegNumber } from "utils/converter";

interface DuplicateCheckDialogProps {
  isOpen: boolean;
  organizationId: string | null;
  // 사업자+귀속연월+세목이 같은 다른 납부서가 있어 중복으로 판정된 납부서 목록
  // (요청한 id 외에 이미 DB에 있던 같은 조합의 다른 납부서도 포함될 수 있음)
  duplicateList: PaymentNotice[];
  businessList: MatchCandidateBusiness[];
  onClose: () => void;
  // 이 팝업에서 삭제/매칭 변경을 하면, 급여관리 메인 화면의 사업자별 현황을
  // 갱신할 수 있도록 알려준다.
  onChanged?: () => void;
  // 이 팝업에서 매칭을 변경/삭제한 건이, 업로드 직후 대기 중인 결과 화면 목록에도
  // 반영되도록 건별로 알려준다 (업로드 흐름이 아니면 상위에서 무시해도 무방).
  onNoticeRematched?: (paymentNoticeId: string, businessId: string) => void;
  onNoticeDeleted?: (paymentNoticeId: string) => void;
}

function DuplicateCheckDialog({
  isOpen,
  organizationId,
  duplicateList,
  businessList,
  onClose,
  onChanged,
  onNoticeRematched,
  onNoticeDeleted,
}: DuplicateCheckDialogProps) {
  const [localList, setLocalList] = useState<PaymentNotice[]>([]);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // 행별로 새로 고른 사업자를 임시로 담아두고, "확인"을 눌렀을 때 한 번에
  // 일괄 매칭 변경 API를 호출한다 (기존 사업자와 같아 실제로 바뀐 게 없는
  // 항목은 제출 대상에서 제외한다).
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });
  // 그룹 표시 순서는 최초 로드 시점의 등장 순서로 고정한다. localList에서 매번
  // 다시 계산하면 그룹 내 앞선 항목을 삭제/매칭 변경할 때 남은 항목의 최초 등장
  // 위치가 바뀌면서 그룹 순서 자체가 흔들려 보이는 문제가 있었다.
  const groupOrderRef = useRef<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setLocalList(duplicateList);

      const order: string[] = [];
      const seen = new Set<string>();
      duplicateList.forEach((notice) => {
        const key = `${notice.businessId}|${notice.period}|${notice.taxItem}`;
        if (!seen.has(key)) {
          seen.add(key);
          order.push(key);
        }
      });
      groupOrderRef.current = order;
    }
  }, [isOpen, duplicateList]);

  const businessNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    businessList.forEach((business) => {
      map[business.id] = business.name;
    });
    return map;
  }, [businessList]);

  const businessOptions: ExtendedOption[] = useMemo(
    () =>
      businessList.map((business) => ({
        value: business.id,
        label: `${business.name} (${convertRegNumber(business.regNumber)})`,
      })),
    [businessList]
  );

  // 사업자+귀속연월+세목이 같은 건끼리 묶어서 보여준다. 그룹 자체의 순서는
  // groupOrderRef에 고정된 순서를 따르고, 여기서는 각 그룹에 남아있는 항목만
  // 채워 넣는다 (모두 삭제/매칭 변경된 그룹은 자연히 제외된다).
  const groups = useMemo(() => {
    const map = new Map<string, PaymentNotice[]>();
    localList.forEach((notice) => {
      const key = `${notice.businessId}|${notice.period}|${notice.taxItem}`;
      const list = map.get(key) || [];
      list.push(notice);
      map.set(key, list);
    });
    return groupOrderRef.current
      .map((key) => map.get(key))
      .filter((list): list is PaymentNotice[] => Boolean(list && list.length > 0));
  }, [localList]);

  const closeDialog = () => {
    if (isSubmitting) return;
    onClose();
    setTimeout(() => {
      setLocalList([]);
      setDeleteTargetId(null);
      setSelections({});
    }, 300);
  };

  const selectBusiness = (noticeId: string) => (businessId: string) => {
    setSelections((prev) => ({ ...prev, [noticeId]: businessId }));
  };

  // 실제로 기존 사업자와 다르게 바뀐 항목만 제출 대상으로 삼는다.
  const changedItems = localList.filter((notice) => {
    const selected = selections[notice.id];
    return selected && selected !== notice.businessId;
  });

  // "확인"을 누르면 바뀐 항목이 있을 때만 일괄 매칭 변경 API를 호출하고, 모두
  // 성공하면 창을 닫는다. 실패한 건이 있으면 재시도할 수 있도록 창을 열어둔다.
  const confirmRematch = async () => {
    if (isSubmitting) return;

    if (!organizationId || changedItems.length === 0) {
      closeDialog();
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await matchPaymentNotice(
        organizationId,
        changedItems.map((notice) => ({
          paymentNoticeId: notice.id,
          businessId: selections[notice.id],
        }))
      );
      const results: PaymentNoticeMatchResult[] = res.data || [];
      const succeededIds = new Set(
        results.filter((result) => result.success).map((r) => r.paymentNoticeId)
      );
      const failedResults = results.filter((result) => !result.success);

      if (succeededIds.size > 0) {
        succeededIds.forEach((id) => {
          onNoticeRematched?.(id, selections[id]);
        });
        setLocalList((prev) =>
          prev.filter((notice) => !succeededIds.has(notice.id))
        );
        setSelections((prev) => {
          const next = { ...prev };
          succeededIds.forEach((id) => delete next[id]);
          return next;
        });
        onChanged?.();
      }

      if (failedResults.length > 0) {
        const failedItems = failedResults.map((result) => {
          const notice = changedItems.find(
            (item) => item.id === result.paymentNoticeId
          );
          const fileName = notice?.originalFileName || result.paymentNoticeId;
          return result.errorMessage
            ? `${fileName} (${result.errorMessage})`
            : fileName;
        });
        setAlertDialogData({
          message: `다음 납부서의 매칭 변경에 실패했습니다: ${failedItems.join(
            ", "
          )}`,
          type: "error",
        });
        setIsAlertOpen(true);
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
      closeDialog();
    } catch (err: any) {
      setAlertDialogData({
        message:
          err.response?.data?.message || "매칭 변경 중 오류가 발생했습니다.",
        type: "error",
      });
      setIsAlertOpen(true);
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTargetId || isDeleting) return;

    setIsDeleting(true);
    try {
      await deletePaymentNotice(deleteTargetId);
      onNoticeDeleted?.(deleteTargetId);
      setLocalList((prev) =>
        prev.filter((notice) => notice.id !== deleteTargetId)
      );
      setSelections((prev) => {
        const next = { ...prev };
        delete next[deleteTargetId];
        return next;
      });
      setDeleteTargetId(null);
      onChanged?.();
      setAlertDialogData({ message: "삭제되었습니다.", type: "success" });
      setIsAlertOpen(true);
    } catch (err: any) {
      setAlertDialogData({
        message:
          err.response?.data?.message || "납부서 삭제 중 오류가 발생했습니다.",
        type: "error",
      });
      setIsAlertOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>중복 의심 납부서 ({localList.length}건)</DialogTitle>
        <DialogContent>
          {localList.length === 0 ? (
            <div
              css={css`
                padding: 32px 0;
                text-align: center;
                font-size: 14px;
                color: ${PALETTE.textMid};
              `}
            >
              중복된 납부서가 없습니다.
            </div>
          ) : (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 16px;
              `}
            >
              {groups.map((group, groupIdx) => {
                const first = group[0];
                return (
                  <div
                    key={groupIdx}
                    css={css`
                      border: 1px solid ${PALETTE.border};
                      border-radius: 8px;
                    `}
                  >
                    <div
                      css={css`
                        padding: 10px 14px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        background-color: ${PALETTE.background};
                        border-radius: 8px 8px 0 0;
                        font-size: 13px;
                        font-weight: 600;
                        color: ${PALETTE.text};
                      `}
                    >
                      <span>
                        {businessNameMap[first.businessId || ""] ||
                          "(알 수 없는 사업자)"}
                      </span>
                      <span
                        css={css`
                          font-weight: 400;
                          color: ${PALETTE.textMid};
                        `}
                      >
                        {first.period} · {first.taxItem}
                      </span>
                    </div>
                    {group.map((notice) => (
                      <div
                        key={notice.id}
                        css={css`
                          padding: 12px 14px;
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: 12px;
                          border-top: 1px solid ${PALETTE.borderLight};
                        `}
                      >
                        <div
                          css={css`
                            min-width: 0;
                            display: flex;
                            flex-direction: column;
                            gap: 2px;
                          `}
                        >
                          <div
                            css={css`
                              font-size: 13px;
                              color: ${PALETTE.text};
                              overflow: hidden;
                              text-overflow: ellipsis;
                              white-space: nowrap;
                            `}
                          >
                            {notice.originalFileName}
                          </div>
                          <div
                            css={css`
                              font-size: 12px;
                              color: ${PALETTE.textMid};
                            `}
                          >
                            {convertCurrency(notice.amount)}원 ·{" "}
                            {convertDate(notice.createdDate, "YYYY.MM.DD")}{" "}
                            업로드
                          </div>
                        </div>
                        <div
                          css={css`
                            display: flex;
                            align-items: center;
                            gap: 6px;
                            flex-shrink: 0;
                          `}
                        >
                          <BusinessAutocomplete
                            placeholder="변경할 사업자 검색"
                            value={selections[notice.id] || notice.businessId || ""}
                            options={businessOptions}
                            disabled={isSubmitting}
                            onSelect={selectBusiness(notice.id)}
                          />
                          <IconButton
                            title="납부서 삭제"
                            size="small"
                            disabled={isSubmitting}
                            onClick={() => setDeleteTargetId(notice.id)}
                          >
                            <DeleteOutlineIcon
                              fontSize="small"
                              css={css`
                                color: ${PALETTE.error};
                              `}
                            />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
        <div
          css={css`
            margin: 6px 24px;
            border-top: 1px solid ${PALETTE.border};
          `}
        />
        <DialogActions
          css={css`
            padding: 12px 24px 20px 24px;
            display: flex;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              font-size: 13px;
              color: ${PALETTE.textMid};
            `}
          >
            {changedItems.length > 0 && `${changedItems.length}건 매칭 변경 예정`}
          </span>
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            disabled={isSubmitting}
            onClick={confirmRematch}
          >
            {isSubmitting ? "처리 중" : "확인"}
          </BaseButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTargetId)}
        onClose={() => !isDeleting && setDeleteTargetId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>알림</DialogTitle>
        <DialogContent>
          <DialogContentText>이 납부서를 삭제하시겠습니까?</DialogContentText>
          <DialogContentText>삭제한 데이터는 복구할 수 없습니다.</DialogContentText>
        </DialogContent>
        <DialogActions
          css={css`
            margin: 0 12px;
            padding: 12px;
            border-top: 1px solid ${PALETTE.border};
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            disabled={isDeleting}
            onClick={() => setDeleteTargetId(null)}
          >
            취소
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.error}
            disabled={isDeleting}
            onClick={confirmDelete}
          >
            {isDeleting ? "삭제 중" : "삭제"}
          </BaseButton>
        </DialogActions>
      </Dialog>

      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </>
  );
}

export default DuplicateCheckDialog;
